
import { useState, useCallback } from "react";
import { CheckSquare, Trash2 } from "lucide-react";
import { CrmTaskStatus, CrmTaskPriority, type CrmTask } from "@/types/crm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getStatusClasses } from "@/lib/status-variants";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { AskAiButton } from "@/components/assistant/assistant-provider";

const statusLabels: Record<CrmTaskStatus, string> = {
  [CrmTaskStatus.Open]: "Open",
  [CrmTaskStatus.InProgress]: "In Progress",
  [CrmTaskStatus.Completed]: "Completed",
};

const priorityLabels: Record<CrmTaskPriority, string> = {
  [CrmTaskPriority.Low]: "Low",
  [CrmTaskPriority.Medium]: "Medium",
  [CrmTaskPriority.High]: "High",
};

interface TaskDetailHeaderProps {
  task: CrmTask;
  onSaveTitle?: (title: string) => void;
  onDelete?: () => void;
}

export function TaskDetailHeader({ task, onSaveTitle, onDelete }: TaskDetailHeaderProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const startEdit = useCallback(() => {
    if (!onSaveTitle) return;
    setDraft(task.title);
    setEditing(true);
  }, [task.title, onSaveTitle]);

  const commit = useCallback(() => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== task.title) {
      onSaveTitle?.(trimmed);
    }
  }, [draft, task.title, onSaveTitle]);

  const cancel = useCallback(() => {
    setEditing(false);
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") { e.preventDefault(); commit(); }
      else if (e.key === "Escape") { e.preventDefault(); cancel(); }
    },
    [commit, cancel]
  );

  return (
    <div className="space-y-1.5">
      <Breadcrumb
        items={[
          { label: "Tasks", href: "/dashboard/tasks" },
          { label: task.title },
        ]}
      />
      <div className="flex items-center gap-2 flex-wrap">
        <CheckSquare className="h-5 w-5 text-muted-foreground shrink-0" />
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={commit}
            className="text-2xl font-semibold tracking-tight bg-transparent border-b-2 border-primary outline-none w-full max-w-md"
          />
        ) : (
          <h1
            className={`text-2xl font-semibold tracking-tight truncate transition-opacity ${onSaveTitle ? "cursor-text hover:opacity-80" : ""}`}
            onClick={startEdit}
            title={onSaveTitle ? "Click to edit" : undefined}
          >
            {task.title}
          </h1>
        )}
        <Badge className={getStatusClasses(statusLabels[task.status])}>
          {statusLabels[task.status]}
        </Badge>
        <Badge className={getStatusClasses(priorityLabels[task.priority])}>
          {priorityLabels[task.priority]}
        </Badge>
        <div className="ml-auto flex items-center gap-1">
          <AskAiButton />
          {onDelete && (
            <Button variant="ghost" size="icon" className="size-9 text-muted-foreground hover:text-destructive" onClick={onDelete} title="Delete task">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      {task.description && (
        <p className="text-sm text-muted-foreground">{task.description}</p>
      )}
    </div>
  );
}
